import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminApi, type Company, type Invitation } from "../api/admin";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { INTERNAL_ROLES, INVITABLE_INTERNAL_ROLES, CUSTOMER_ROLES, SUPPLIER_ROLES } from "../auth/roles";
import { COUNTRIES, flagEmoji } from "../i18n/countries";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../i18n";
import { localeFlag, localeLabel } from "../i18n/LocaleSwitcher";
import { Select } from "./Select";

export function InviteUserForm({
  onCreated,
  onCancel,
  preselectedCompanyId,
}: {
  onCreated: (inv: Invitation) => void;
  onCancel?: () => void;
  preselectedCompanyId?: string | null;
}) {
  const { t } = useTranslation();
  const { state, hasPermission } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);

  // Save posts to /api/admin/invitations, which asks for section:invitations:write
  // together with users:invite_any. Reading the register is the weaker
  // section:invitations:read, so the form must never be drawn on that alone.
  const canSend = hasPermission("section:invitations:write") && hasPermission("users:invite_any");
  // There are four invite permissions and they are not interchangeable — the kind
  // of role being invited decides which one the server consults, so each role
  // group is offered only to the holder of its own permission.
  //
  // Internal staff additionally needs access_control:manage: the service demands
  // it for every internal role, so without it the group would only produce a 403.
  const canInviteInternal =
    hasPermission("users:invite_internal_user") && hasPermission("access_control:manage");
  // users:invite_own_company belongs to the customer manager alone and binds the
  // invite to their own company; users:invite_customer_any_company is the
  // sales-side counterpart with a free choice of company.
  const canInviteCustomerAnyCompany = hasPermission("users:invite_customer_any_company");
  const canInviteOwnCompany = hasPermission("users:invite_own_company");
  const ownCompanyOnly = canInviteOwnCompany && !canInviteCustomerAnyCompany;
  // The otkup referent onboards their cooperants, so supplier roles follow
  // users:invite_supplier_assigned; the admin tier holds them too.
  const canInviteSupplier = hasPermission("users:invite_supplier_assigned") || canInviteInternal;

  useEffect(() => {
    if (!canSend) return;
    void adminApi
      .listCompanies(0, 200)
      .then((p) => setCompanies(p.content.filter(Boolean)))
      .catch(() => setCompanies([]));
  }, [canSend]);

  if (state.status !== "authenticated") return null;

  const allowedRoles: string[] = [
    ...(canInviteInternal ? INVITABLE_INTERNAL_ROLES : []),
    ...(canInviteCustomerAnyCompany || canInviteOwnCompany ? CUSTOMER_ROLES : []),
    ...(canInviteSupplier ? SUPPLIER_ROLES : []),
  ];

  // No form rather than a form that ends in a refusal — and a sentence in its
  // place, so the reader knows an invitation is sent elsewhere and not broken here.
  if (!canSend || allowedRoles.length === 0) {
    return (
      <p style={notice}>
        {t(
          "inviteUser.noPermission",
          "Pozivnice ovde samo čitate. Poziv šalje administrator korisnika, ili komercijalista za svoje firme.",
        )}
      </p>
    );
  }

  const lockedCompanyId = ownCompanyOnly
    ? state.user.companyId ?? undefined
    : preselectedCompanyId ?? undefined;

  return (
    <Form
      companies={
        lockedCompanyId ? companies.filter((c) => c.id === lockedCompanyId) : companies
      }
      allowedRoles={allowedRoles}
      defaultCompanyId={lockedCompanyId}
      lockCompany={!!lockedCompanyId}
      canInviteInternal={canInviteInternal}
      onCreated={onCreated}
      onCancel={onCancel}
    />
  );
}

function Form({
  companies,
  allowedRoles,
  defaultCompanyId,
  lockCompany,
  canInviteInternal,
  onCreated,
  onCancel,
}: {
  companies: Company[];
  allowedRoles: string[];
  defaultCompanyId?: string;
  lockCompany?: boolean;
  canInviteInternal?: boolean;
  onCreated: (inv: Invitation) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [companyId, setCompanyId] = useState(defaultCompanyId ?? "");
  const [roles, setRoles] = useState<string[]>([]);
  const [locale, setLocale] = useState<SupportedLocale>("en");
  const [erpId, setErpId] = useState("");
  const [maxDiscount, setMaxDiscount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleRole(r: string) {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (roles.length === 0) {
      setError(t("inviteUser.error.pickRole"));
      return;
    }
    setSubmitting(true);
    try {
      const inv = await adminApi.createInvitation({
        email: email.trim(),
        companyId: companyId || undefined,
        roles,
        locale,
        erpId: showInternalFields && erpId.trim() ? erpId.trim() : undefined,
        maxDiscountPct: salesRolePicked && maxDiscount.trim() !== "" ? Number(maxDiscount) : undefined,
      });
      onCreated(inv);
      setEmail("");
      setRoles([]);
    } catch (err) {
      setError(translateError(err as ApiError, t));
    } finally {
      setSubmitting(false);
    }
  }

  const customerRolePicked = roles.some((r) => (CUSTOMER_ROLES as readonly string[]).includes(r));
  const supplierRolePicked = roles.some((r) => (SUPPLIER_ROLES as readonly string[]).includes(r));
  // Customer AND supplier roles are company-scoped — both require a company
  // to be picked (mirrors backend COMPANY_REQUIRED for either kind).
  const companyRequiredRolePicked = customerRolePicked || supplierRolePicked;
  // ERP ID is an internal-staff-only concept. Show it only to inviters who can
  // create internal users (admins), and never once a customer or supplier
  // role is in play. Customers/suppliers never see it.
  const showInternalFields = !!canInviteInternal && !companyRequiredRolePicked;
  // The max-discount cap only applies to sales roles.
  const salesRolePicked = roles.includes("SALES") || roles.includes("SALES_DIRECTOR");

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Field label={t("inviteUser.field.email")} required>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={input}
          placeholder={t("inviteUser.field.emailPlaceholder")}
        />
      </Field>

      <Field label={t("inviteUser.field.company")}>
        <Select<string>
          value={companyId}
          onChange={setCompanyId}
          disabled={lockCompany}
          ariaLabel={t("inviteUser.field.company")}
          options={[
            {
              value: "",
              label: `Bankom d.o.o. (${t("inviteUser.field.companyInternalStaff", { defaultValue: "internal" })})`,
              left: flagEmoji("RS"),
            },
            ...companies.map((c) => ({
              value: c.id,
              label: c.country
                ? `${c.name} (${COUNTRIES.find((x) => x.code === c.country)?.name ?? c.country})`
                : c.name,
              left: c.country ? flagEmoji(c.country) : undefined,
            })),
          ]}
        />
        {companyRequiredRolePicked && !companyId && (
          <span style={{ color: "var(--err)", fontSize: 12, marginTop: 4 }}>
            {t("inviteUser.companyRequiredHint", "Customer and supplier roles require a company.")}
          </span>
        )}
      </Field>

      <Field label={t("inviteUser.field.roles")} required>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {allowedRoles.some((r) => (INTERNAL_ROLES as readonly string[]).includes(r)) && (
            <RoleGroup
              title={t("inviteUser.field.roles.internal", "Interno (Bankom)")}
              roles={allowedRoles.filter((r) => (INTERNAL_ROLES as readonly string[]).includes(r))}
              selected={roles}
              onToggle={toggleRole}
            />
          )}
          {allowedRoles.some((r) => (CUSTOMER_ROLES as readonly string[]).includes(r)) && (
            <RoleGroup
              title={t("inviteUser.field.roles.customer", "Customer")}
              roles={allowedRoles.filter((r) => (CUSTOMER_ROLES as readonly string[]).includes(r))}
              selected={roles}
              onToggle={toggleRole}
            />
          )}
          {allowedRoles.some((r) => (SUPPLIER_ROLES as readonly string[]).includes(r)) && (
            <RoleGroup
              title={t("inviteUser.field.roles.supplier", "Supplier")}
              roles={allowedRoles.filter((r) => (SUPPLIER_ROLES as readonly string[]).includes(r))}
              selected={roles}
              onToggle={toggleRole}
            />
          )}
        </div>
      </Field>

      {showInternalFields && (
        <Field label={t("inviteUser.field.erpId", { defaultValue: "ERP ID" })}>
          <input
            value={erpId}
            onChange={(e) => setErpId(e.target.value)}
            style={input}
            maxLength={64}
            placeholder={t("inviteUser.field.erpIdPlaceholder", { defaultValue: "optional" })}
          />
        </Field>
      )}

      {salesRolePicked && (
        <Field label={t("inviteUser.field.maxDiscount", { defaultValue: "Max discount %" })}>
          <input
            type="number"
            min={0}
            max={100}
            step="0.5"
            value={maxDiscount}
            onChange={(e) => setMaxDiscount(e.target.value)}
            style={{ ...input, width: 120 }}
            placeholder={t("inviteUser.field.maxDiscountPlaceholder", { defaultValue: "no limit" })}
          />
          <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
            {t("inviteUser.field.maxDiscountHint", {
              defaultValue: "Highest discount this rep can grant to customers on orders and offers. Blank = no limit.",
            })}
          </span>
        </Field>
      )}

      <Field label={t("inviteUser.field.emailLanguage")}>
        <Select<SupportedLocale>
          value={locale}
          onChange={setLocale}
          ariaLabel={t("inviteUser.field.emailLanguage")}
          options={SUPPORTED_LOCALES.map((l) => ({
            value: l,
            label: localeLabel(l),
            left: <span style={{ fontFamily: emojiFontStack, lineHeight: 1 }}>{localeFlag(l)}</span>,
          }))}
        />
      </Field>

      {error && <div style={errorBox}>{error}</div>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {onCancel && (
          <button type="button" onClick={onCancel} style={secondary}>
            {t("inviteUser.cancel")}
          </button>
        )}
        <button type="submit" disabled={submitting} style={primary}>
          {submitting ? t("inviteUser.submitBusy") : t("inviteUser.submit")}
        </button>
      </div>
    </form>
  );
}

function translateError(err: ApiError, t: (key: string) => string): string {
  switch (err.code) {
    case "USER_EXISTS":
      return t("inviteUser.error.userExists");
    case "INVITATION_PENDING":
      return t("inviteUser.error.invitationPending");
    case "FORBIDDEN":
      return t("inviteUser.error.forbidden");
    case "COMPANY_REQUIRED":
      return t("inviteUser.error.companyRequired");
    default:
      return err.message || t("inviteUser.error.generic");
  }
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const emojiFontStack =
  'apple color emoji, segoe ui emoji, noto color emoji, "Inter Tight", system-ui, sans-serif';
const input: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  fontSize: 14,
};
const primary: React.CSSProperties = {
  padding: "8px 14px",
  background: "var(--brand-primary-600)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 6,
  fontSize: 14,
  cursor: "pointer",
};
const secondary: React.CSSProperties = {
  padding: "8px 14px",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  fontSize: 14,
  cursor: "pointer",
};
const errorBox: React.CSSProperties = {
  padding: 10,
  background: "var(--err-bg)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 13,
};
const notice: React.CSSProperties = {
  margin: 0,
  color: "var(--ink-3)",
  fontSize: 13,
};

function RoleGroup({
  title, roles, selected, onToggle,
}: {
  title: string;
  roles: string[];
  selected: string[];
  onToggle: (r: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      border: "1px solid var(--border)",
      borderRadius: 6,
      padding: "8px 10px",
      background: "var(--surface)",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700,
        color: "var(--ink-3)",
        letterSpacing: ".04em", textTransform: "uppercase",
        marginBottom: 6,
      }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {roles.map((r) => (
          <label key={r} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={selected.includes(r)} onChange={() => onToggle(r)} />
            {t(`admin.users.role.${r}`, r)}
          </label>
        ))}
      </div>
    </div>
  );
}
